// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './Pomodoro.css';
import { NavLink } from 'react-router-dom';

const SHORT_BREAK_TIME = 5 * 60;
const LONG_BREAK_TIME = 10 * 60;

function getZonedTimeParts(date) {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
        const parts = formatter.formatToParts(date);
        const map = {};
        parts.forEach(p => map[p.type] = p.value);

        const rawHours = parseInt(map.hour, 10);
        const hours = isNaN(rawHours) ? date.getHours() : rawHours % 24;
        const minutes = isNaN(parseInt(map.minute, 10)) ? date.getMinutes() : parseInt(map.minute, 10);
        const seconds = isNaN(parseInt(map.second, 10)) ? date.getSeconds() : parseInt(map.second, 10);
        const milliseconds = date.getMilliseconds();
        const dayStr = map.weekday || '';
        const dayNum = map.day || '';
        const monthStr = map.month || '';

        return {
            hours,
            minutes,
            seconds,
            milliseconds,
            dateStr: (dayStr && dayNum && monthStr) ? `${dayStr}, ${dayNum} ${monthStr}` : null,
            timeZone: tz
        };
    } catch (e) {
        return {
            hours: date.getHours(),
            minutes: date.getMinutes(),
            seconds: date.getSeconds(),
            milliseconds: date.getMilliseconds(),
            dateStr: null,
            timeZone: null
        };
    }
}

function Pomodoro() {
    const [view, setView] = useState('pomodoro'); // pomodoro or clock
    const [pomodoroDuration, setPomodoroDuration] = useState(25 * 60); // Default 25
    const [timer, setTimer] = useState(pomodoroDuration);
    const [isActive, setIsActive] = useState(false);
    const [mode, setMode] = useState('pomodoro'); // pomodoro, short, long
    const [theme, setTheme] = useState('rose'); // rose, grid

    // Clock States
    const [clockMode, setClockMode] = useState('Radio'); // 'Digital', 'Radio', 'Orbit', 'Analog', 'Big'
    const [sweepMode, setSweepMode] = useState('Quartz'); // 'Quartz', 'Mechanical'
    const [clockTheme, setClockTheme] = useState('blue'); // 'white', 'dark', 'green', 'yellow', 'blue'
    const [soundMode, setSoundMode] = useState('Mute'); // 'Mute', 'System', 'Watch'
    const [volume, setVolume] = useState(100);
    const [showSidebar, setShowSidebar] = useState(true);
    const [time, setTime] = useState(new Date());
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Audio context for ticking sound synthesis
    const audioCtxRef = useRef(null);
    const prevSecRef = useRef(-1);
    const timeOffsetRef = useRef(0);

    // Automatically sync exact location time via IP/timezone network API to bypass device clock inaccuracies
    useEffect(() => {
        const syncNetworkTime = async () => {
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                let res = await fetch(`https://worldtimeapi.org/api/timezone/${tz}`).catch(() => null);
                if (!res || !res.ok) {
                    res = await fetch('https://worldtimeapi.org/api/ip').catch(() => null);
                }
                if (res && res.ok) {
                    const data = await res.json();
                    if (data && data.datetime) {
                        const serverMs = new Date(data.datetime).getTime();
                        const clientMs = Date.now();
                        timeOffsetRef.current = serverMs - clientMs;
                    }
                }
            } catch (e) {
                console.warn("Location time network sync fallback:", e);
            }
        };
        syncNetworkTime();
    }, []);

    // Task State
    const [tasks, setTasks] = useState([
        { id: 1, text: "Website Development", completed: false, active: true },
        // { id: 2, text: "Read Documentation", completed: false, active: false }
    ]);
    const [newTaskText, setNewTaskText] = useState("");
    const [isAddingTask, setIsAddingTask] = useState(false);

    const timerRef = useRef(null);

    // Update timer when duration setting changes (only if in pomodoro mode and not active)
    useEffect(() => {
        if (mode === 'pomodoro' && !isActive) {
            setTimer(pomodoroDuration);
        }
    }, [pomodoroDuration, mode, isActive]);

    useEffect(() => {
        if (isActive && timer > 0) {
            timerRef.current = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsActive(false);
            clearInterval(timerRef.current);
            // Play alarm sound here if desired
        }

        return () => clearInterval(timerRef.current);
    }, [isActive, timer]);

    // Keep clock time updated continuously in foreground and background tabs
    useEffect(() => {
        let animId;
        const updateTime = () => {
            setTime(new Date(Date.now() + timeOffsetRef.current));
        };

        const loop = () => {
            updateTime();
            animId = requestAnimationFrame(loop);
        };
        animId = requestAnimationFrame(loop);

        // setInterval fallback ensures tab title & time keep updating live when tab is in background
        const intervalId = setInterval(updateTime, 500);

        // Instantly update time when tab becomes visible again
        const handleVisibilityChange = () => {
            updateTime();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelAnimationFrame(animId);
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Synthesis helper for watch ticking sound using AudioContext
    const playTickSound = (type = 1000) => {
        try {
            if (!audioCtxRef.current) {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                if (AudioContextClass) {
                    audioCtxRef.current = new AudioContextClass();
                }
            }
            const ctx = audioCtxRef.current;
            if (!ctx) return;
            if (ctx.state === 'suspended') {
                ctx.resume();
            }
            
            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            const gainNode = ctx.createGain();
            
            osc.connect(gainNode);
            gainNode.connect(ctx.destination);
            
            if (type === 1000) {
                // Quartz standard click
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(800, now);
                osc.frequency.exponentialRampToValueAtTime(100, now + 0.03);
                
                gainNode.gain.setValueAtTime((volume / 100) * 0.2, now);
                gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
                osc.start(now);
                osc.stop(now + 0.04);
            } else {
                // Mechanical sweeping escapement tick (rapid)
                osc.type = 'sine';
                osc.frequency.setValueAtTime(1200, now);
                osc.frequency.exponentialRampToValueAtTime(400, now + 0.015);
                
                gainNode.gain.setValueAtTime((volume / 100) * 0.12, now);
                gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.015);
                osc.start(now);
                osc.stop(now + 0.02);
            }
        } catch (e) {
            console.warn("Audio Context failed to play tick", e);
        }
    };

    const playSystemSound = () => {
        try {
            if (!audioCtxRef.current) {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                if (AudioContextClass) {
                    audioCtxRef.current = new AudioContextClass();
                }
            }
            const ctx = audioCtxRef.current;
            if (!ctx) return;
            if (ctx.state === 'suspended') {
                ctx.resume();
            }
            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            const gainNode = ctx.createGain();
            osc.connect(gainNode);
            gainNode.connect(ctx.destination);
            
            osc.type = 'sine';
            osc.frequency.setValueAtTime(1000, now);
            gainNode.gain.setValueAtTime((volume / 100) * 0.08, now);
            gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
            osc.start(now);
            osc.stop(now + 0.06);
        } catch (e) {
            console.warn("Audio Context failed to play system sound", e);
        }
    };

    // Handle audio tick scheduling for Quartz/System
    useEffect(() => {
        const currentSec = time.getSeconds();
        if (currentSec !== prevSecRef.current) {
            prevSecRef.current = currentSec;
            if (view === 'clock') {
                if (soundMode === 'Watch' && sweepMode === 'Quartz') {
                    playTickSound(1000);
                } else if (soundMode === 'System') {
                    playSystemSound();
                }
            }
        }
    }, [time, soundMode, sweepMode, view]);

    // Mechanical sweeping ticking sound loop
    useEffect(() => {
        if (view !== 'clock' || soundMode !== 'Watch' || sweepMode !== 'Mechanical') return;
        
        const interval = setInterval(() => {
            playTickSound(3000);
        }, 200); // 5Hz sweep rate ticking
        
        return () => clearInterval(interval);
    }, [view, soundMode, sweepMode, volume]);

    // Fullscreen listeners
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setIsActive(false);
        if (newMode === 'pomodoro') setTimer(pomodoroDuration);
        else if (newMode === 'short') setTimer(SHORT_BREAK_TIME);
        else if (newMode === 'long') setTimer(LONG_BREAK_TIME);
    };

    const handleDurationChange = (minutes) => {
        setPomodoroDuration(minutes * 60);
        // Effect will handle timer update
    };

    const toggleTimer = () => {
        setIsActive(!isActive);
    };

    const adjustTimer = (amount) => {
        setTimer(prev => Math.max(0, prev + amount));
    };

    const resetTimer = () => {
        setIsActive(false);
        if (mode === 'pomodoro') setTimer(pomodoroDuration);
        else if (mode === 'short') setTimer(SHORT_BREAK_TIME);
        else if (mode === 'long') setTimer(LONG_BREAK_TIME);
    };

    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    // Task Handlers
    const addTask = () => {
        if (newTaskText.trim()) {
            // Add new task to the top
            setTasks([{ id: Date.now(), text: newTaskText, completed: false, active: false }, ...tasks]);
            setNewTaskText("");
            setIsAddingTask(false);
        }
    };

    const toggleTaskComplete = (id) => {
        const taskIndex = tasks.findIndex(t => t.id === id);
        if (taskIndex === -1) return;

        const updatedTasks = [...tasks];
        const task = { ...updatedTasks[taskIndex], completed: !updatedTasks[taskIndex].completed };
        
        // Remove from current position
        updatedTasks.splice(taskIndex, 1);

        if (task.completed) {
            // Move to bottom
            updatedTasks.push(task);
        } else {
            // Move to top (or just below active?) - moving to top for now
            updatedTasks.unshift(task);
        }

        setTasks(updatedTasks);
    };

    const deleteTask = (e, id) => {
        e.stopPropagation(); // Prevent toggling completion
        setTasks(tasks.filter(t => t.id !== id));
    };

    // Drag and Drop Handlers
    const handleDragStart = (index) => {
        setDraggedItemIndex(index);
    };

    const handleDragEnter = (index) => {
        if (draggedItemIndex === null || draggedItemIndex === index) return;
        
        const updatedTasks = [...tasks];
        const draggedItem = updatedTasks[draggedItemIndex];
        
        // Remove dragged item
        updatedTasks.splice(draggedItemIndex, 1);
        // Insert at new position
        updatedTasks.splice(index, 0, draggedItem);
        
        setDraggedItemIndex(index);
        setTasks(updatedTasks);
    };

    const handleDragEnd = () => {
        setDraggedItemIndex(null);
    };

    // Active task is simply the first task in the list
    const activeTask = tasks.length > 0 ? tasks[0] : null;

    // RENDER CLOCK VIEW
    if (view === 'clock') {
        const zoned = getZonedTimeParts(time);
        const hours = zoned.hours;
        const minutes = zoned.minutes;
        const seconds = zoned.seconds;
        const milliseconds = zoned.milliseconds;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const formattedHours = displayHours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = seconds.toString().padStart(2, '0');

        // Angles for clock hands
        const hourAngle = (hours % 12) * 30 + minutes * 0.5;
        const minuteAngle = minutes * 6 + seconds * 0.1;
        const secondAngle = sweepMode === 'Mechanical' 
            ? (seconds + milliseconds / 1000) * 6 
            : seconds * 6;

        // Ruler position calculation for Radio Clock
        const currentVal = seconds + (sweepMode === 'Mechanical' ? milliseconds / 1000 : 0);
        const totalVal = 60 + currentVal;
        const tickHeight = 24;
        const rulerTranslateY = `${150 - (totalVal + 0.5) * tickHeight}px`;

        const getFormattedDate = () => {
            if (zoned.dateStr) return zoned.dateStr;
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${days[time.getDay()]}, ${time.getDate()} ${months[time.getMonth()]}`;
        };

        return (
            <div className={`clock-container clock-theme-${clockTheme} relative min-h-screen w-full flex overflow-hidden font-questrial`}>
                <Helmet>
                    <title>{`${formattedHours}:${formattedMinutes}:${formattedSeconds} - Clock`}</title>
                    <meta name="robots" content="noindex,nofollow" />
                </Helmet>

                {/* TOP LEFT CONTROLS */}
                <div className="absolute top-[30px] left-[30px] z-[120] flex items-center gap-3">
                    <div className="clock-toggle-capsule">
                        <button className="toggle-btn active">
                            <i className="fi fi-rr-clock-two"></i>
                            <span className="toggle-text">Clock</span>
                        </button>
                        <button className="toggle-btn" onClick={() => setView('pomodoro')}>
                            <i className="fi fi-rr-chess-clock-alt"></i>
                            <span className="toggle-text">pomodoro</span>
                        </button>
                    </div>
                    <button className="clock-palette-btn" onClick={() => setShowSidebar(!showSidebar)} title="Toggle settings">
                        <i className="fi fi-rr-settings"></i>
                    </button>
                </div>

                {/* TOP RIGHT CONTROLS */}
                <div className="absolute top-[30px] right-[30px] z-[100] flex items-center gap-3">
                    <button className="clock-palette-btn" onClick={toggleFullscreen} title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                        <i className="fi fi-sr-arrow-up-right-and-arrow-down-left-from-center"></i>
                    </button>
                </div>

                {/* SIDEBAR PREFERENCES */}
                <div className={`clock-sidebar ${showSidebar ? 'open' : 'closed'}`}>
                    <div className="clock-sidebar-content flex flex-col h-full justify-center gap-8 px-8 py-12">
                        {/* SYSTEM PREFERENCES */}
                        <div className="flex flex-col gap-4">
                            <span className="sidebar-section-title">SYSTEM PREFERENCES</span>
                            
                            {/* Layout Selection */}
                            <div className="flex flex-wrap gap-2">
                                {['Digital', 'Radio', 'Orbit', 'Analog', 'Big'].map((modeOpt) => (
                                    <button 
                                        key={modeOpt} 
                                        className={`sidebar-pill-btn ${clockMode === modeOpt ? 'active' : ''}`}
                                        onClick={() => setClockMode(modeOpt)}
                                    >
                                        {modeOpt}
                                    </button>
                                ))}
                            </div>

                            {/* Quartz vs Mechanical Sub-toggle for Analog styles */}
                            {(clockMode === 'Analog' || clockMode === 'Big' || clockMode === 'Orbit') && (
                                <div className="flex gap-2 mt-1">
                                    {['Quartz', 'Mechanical'].map((sweepOpt) => (
                                        <button 
                                            key={sweepOpt} 
                                            className={`sidebar-pill-btn sub-btn ${sweepMode === sweepOpt ? 'active' : ''}`}
                                            onClick={() => setSweepMode(sweepOpt)}
                                        >
                                            {sweepOpt}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Theme Colors selection */}
                            <div className="flex gap-3 mt-2">
                                {['white', 'dark', 'green', 'yellow', 'blue'].map((colorTheme) => (
                                    <button
                                        key={colorTheme}
                                        className={`theme-picker-circle circle-${colorTheme} ${clockTheme === colorTheme ? 'active' : ''}`}
                                        onClick={() => setClockTheme(colorTheme)}
                                        title={`${colorTheme} theme`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* SOUND PREFERENCES */}
                        <div className="flex flex-col gap-4">
                            <span className="sidebar-section-title">SOUND</span>
                            
                            {/* Sound Mode */}
                            <div className="flex gap-2">
                                {['Mute', 'System', 'Watch'].map((soundOpt) => (
                                    <button 
                                        key={soundOpt} 
                                        className={`sidebar-pill-btn ${soundMode === soundOpt ? 'active' : ''}`}
                                        onClick={() => setSoundMode(soundOpt)}
                                    >
                                        {soundOpt}
                                    </button>
                                ))}
                            </div>

                            {/* Volume Slider */}
                            <div className="flex items-center gap-3 w-full">
                                <input 
                                    type="range" 
                                    min="0" 
                                    max="100" 
                                    value={volume}
                                    onChange={(e) => setVolume(parseInt(e.target.value))}
                                    className="clock-volume-slider flex-1"
                                />
                                <span className="volume-label w-8 text-right text-xs font-semibold">{volume}%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CLOCK FACES CONTAINER */}
                <div className="clock-face-viewport absolute inset-0 flex items-center justify-center">
                    <div className="clock-face-wrapper flex items-center justify-center relative w-full h-full">
                        {/* 1. DIGITAL CLOCK */}
                        {clockMode === 'Digital' && (
                            <div className="clock-digital-face flex items-center justify-center text-center">
                                <div className="digital-time-container flex items-baseline">
                                    <span className="digital-time-main select-none font-bold tabular-nums tracking-tighter">
                                        {formattedHours}:{formattedMinutes}
                                    </span>
                                    <div className="digital-time-sec-ampm flex flex-col items-start ml-4 leading-none">
                                        <span className="digital-time-sec select-none font-semibold tabular-nums opacity-90">
                                            {formattedSeconds}
                                        </span>
                                        <span className="digital-time-ampm select-none text-xl font-bold tracking-wide opacity-85 mt-1">
                                            {ampm}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 2. RADIO CLOCK */}
                        {clockMode === 'Radio' && (
                            <div className="clock-radio-face flex items-center justify-center w-full max-w-4xl px-6 select-none font-bold">
                                <div className="radio-left text-right flex-1 tabular-nums font-bold leading-none tracking-tight">
                                    {formattedHours}:{formattedMinutes}
                                </div>
                                
                                <div className="radio-center relative mx-8 w-[80px] h-[300px] overflow-hidden flex justify-center">
                                    <div className="radio-indicator-line"></div>
                                    <div 
                                        className="radio-ruler flex flex-col items-center"
                                        style={{ transform: `translateY(${rulerTranslateY})` }}
                                    >
                                        {Array.from({ length: 180 }).map((_, i) => {
                                            const val = i % 60;
                                            const isMajor = val % 5 === 0;
                                            const isTens = val % 10 === 0;
                                            return (
                                                <div 
                                                    key={i} 
                                                    className={`radio-tick-item ${isMajor ? 'major' : ''} ${isTens ? 'tens' : ''} h-[24px] flex items-center justify-center w-full`}
                                                >
                                                    <div className="radio-tick-line"></div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="radio-right flex-1 text-left flex items-baseline leading-none">
                                    <span className="radio-sec tabular-nums font-bold leading-none tracking-tight mr-3">
                                        {formattedSeconds}
                                    </span>
                                    <span className="radio-ampm text-4xl font-bold opacity-80 uppercase">
                                        {ampm}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* 3. ORBIT CLOCK */}
                        {clockMode === 'Orbit' && (
                            <div className="clock-orbit-face flex items-center justify-center w-full h-full">
                                <svg width="420" height="420" viewBox="-210 -210 420 420" className="orbit-svg select-none">
                                    {/* Concentric circular tracks */}
                                    <circle cx="0" cy="0" r="160" className="orbit-track" />
                                    <circle cx="0" cy="0" r="120" className="orbit-track" />
                                    <circle cx="0" cy="0" r="80" className="orbit-track" />
                                    
                                    {/* Orbiting glow orbs - using rotate(angle, cx, cy) */}
                                    <circle cx="0" cy="-160" r="8" className="orbit-orb hour-orb" transform={`rotate(${hourAngle}, 0, 0)`} />
                                    <circle cx="0" cy="-120" r="6" className="orbit-orb minute-orb" transform={`rotate(${minuteAngle}, 0, 0)`} />
                                    <circle cx="0" cy="-80" r="4.5" className="orbit-orb second-orb" transform={`rotate(${secondAngle}, 0, 0)`} />
                                    
                                    {/* Orbit 12 Hour numbers around the outer track */}
                                    {Array.from({ length: 12 }).map((_, i) => {
                                        const num = i + 1;
                                        const angle = num * 30 * Math.PI / 180;
                                        const x = Math.sin(angle) * 185;
                                        const y = -Math.cos(angle) * 185;
                                        return (
                                            <text 
                                                key={num} 
                                                x={x} 
                                                y={y + 4} 
                                                className="orbit-number-text text-[11px] font-semibold opacity-60"
                                                textAnchor="middle"
                                                fill="currentColor"
                                            >
                                                {num}
                                            </text>
                                        );
                                    })}

                                    {/* Central Digital Readout */}
                                    <text x="0" y="8" className="orbit-center-text font-bold text-2xl" textAnchor="middle">
                                        {formattedHours}:{formattedMinutes}
                                    </text>
                                </svg>
                            </div>
                        )}

                        {/* 4. ANALOG CLOCK */}
                        {clockMode === 'Analog' && (
                            <div className="clock-analog-face flex items-center justify-center w-full h-full select-none">
                                <div className="analog-face-inner relative w-[360px] h-[360px]">
                                    {/* 12 numbers */}
                                    {Array.from({ length: 12 }).map((_, i) => {
                                        const num = i + 1;
                                        const angle = num * 30 * Math.PI / 180;
                                        const x = Math.sin(angle) * 125;
                                        const y = -Math.cos(angle) * 125;
                                        return (
                                            <div 
                                                key={num} 
                                                className="analog-number absolute font-semibold text-2xl flex items-center justify-center" 
                                                style={{ 
                                                    left: '50%',
                                                    top: '50%',
                                                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` 
                                                }}
                                            >
                                                {num}
                                            </div>
                                        );
                                    })}
                                    
                                    {/* 60 ticks */}
                                    {Array.from({ length: 60 }).map((_, i) => {
                                        const isMajor = i % 5 === 0;
                                        return (
                                            <div 
                                                key={i} 
                                                className={`analog-tick absolute ${isMajor ? 'major' : ''}`}
                                                style={{ 
                                                    left: '50%',
                                                    top: '50%',
                                                    transform: `translate(-50%, -50%) rotate(${i * 6}deg) translateY(-155px)` 
                                                }}
                                            />
                                        );
                                    })}
                                    
                                    {/* Clock Hands with guaranteed inline pivot centers */}
                                    <div className="analog-hand hour-hand" style={{ transform: `translate(-50%, -100%) rotate(${hourAngle}deg)`, transformOrigin: '50% 100%' }} />
                                    <div className="analog-hand minute-hand" style={{ transform: `translate(-50%, -100%) rotate(${minuteAngle}deg)`, transformOrigin: '50% 100%' }} />
                                    <div className="analog-hand second-hand" style={{ transform: `translate(-50%, -115px) rotate(${secondAngle}deg)`, transformOrigin: '50% 115px' }} />
                                    <div className="analog-pivot" />
                                </div>
                            </div>
                        )}

                        {/* 5. BIG CLOCK */}
                        {clockMode === 'Big' && (
                            <div className="clock-big-face flex items-center justify-center w-full h-full select-none relative">
                                {/* Giant backdrop numbers */}
                                <div className="big-bg-number big-num-12 font-bold">12</div>
                                <div className="big-bg-number big-num-3 font-bold">3</div>
                                <div className="big-bg-number big-num-6 font-bold">6</div>
                                <div className="big-bg-number big-num-9 font-bold">9</div>
                                
                                {/* Hands with guaranteed inline pivot centers */}
                                <div className="big-hand hour-hand" style={{ transform: `translate(-50%, -100%) rotate(${hourAngle}deg)`, transformOrigin: '50% 100%' }} />
                                <div className="big-hand minute-hand" style={{ transform: `translate(-50%, -100%) rotate(${minuteAngle}deg)`, transformOrigin: '50% 100%' }} />
                                <div className="big-hand second-hand" style={{ transform: `translate(-50%, -155px) rotate(${secondAngle}deg)`, transformOrigin: '50% 155px' }} />
                                <div className="big-pivot" />
                            </div>
                        )}
                    </div>
                </div>

                {/* BOTTOM RIGHT DATE PILL */}
                <div className="absolute bottom-[30px] right-[30px] z-[90] clock-date-wrapper">
                    <div className="clock-date-pill select-none font-bold text-sm tracking-wide">
                        {getFormattedDate()}
                    </div>
                </div>
            </div>
        );
    }

    const zonedPomodoro = getZonedTimeParts(time);
    const pomodoroDisplayHours = (zonedPomodoro.hours % 12 || 12).toString().padStart(2, '0');
    const pomodoroFormattedMinutes = zonedPomodoro.minutes.toString().padStart(2, '0');
    const pomodoroFormattedSeconds = zonedPomodoro.seconds.toString().padStart(2, '0');

    return (
        <div className={`pomodoro-container theme-${theme} relative min-h-screen overflow-hidden px-3 py-6 md:px-6`}>
            <Helmet>
                <title>{isActive ? `${formatTime(timer)} - Pomodoro` : `${pomodoroDisplayHours}:${pomodoroFormattedMinutes}:${pomodoroFormattedSeconds} - Pomodoro`}</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>
            <div className="pomodoro-overlay"></div>
            
            {/* TOP LEFT TOGGLE IN POMODORO MODE */}
            <div className="absolute top-[30px] left-[30px] z-[100] flex items-center gap-3">
                <div className="clock-toggle-capsule">
                    <button 
                        className="toggle-btn"
                        onClick={() => setView('clock')}
                    >
                        <i className="fi fi-rr-clock-two"></i>
                        <span className="toggle-text">Clock</span>
                    </button>
                    <button 
                        className="toggle-btn active"
                    >
                        <i className="fi fi-rr-chess-clock-alt"></i>
                        <span className="toggle-text">pomodoro</span>
                    </button>
                </div>
            </div>
            
            <NavLink to="/app-store" className="home-link">
                <i className="fi fi-rr-arrow-small-left"></i> <span className="home-link-text">App Store</span>
            </NavLink>

            <div className="theme-selector mx-auto mb-5 flex w-full max-w-5xl items-center justify-end gap-3">
                <span className="theme-label">Theme</span>
                <div className="theme-circles">
                    <div 
                        className={`theme-circle circle-rose ${theme === 'rose' ? 'active' : ''}`}
                        onClick={() => setTheme('rose')}
                        title="Rose Sunset"
                    ></div>
                    <div 
                        className={`theme-circle circle-grid ${theme === 'grid' ? 'active' : ''}`}
                        onClick={() => setTheme('grid')}
                        title="Minimal Grid"
                    ></div>
                </div>
            </div>

            <div className="pomodoro-main-layout mx-auto grid w-full max-w-5xl gap-5 lg:grid-cols-[1.1fr_1fr]">
                {/* TIMER SECTION */}
                <div className="pomodoro-timer-section">
                    {/* Large Timer Display */}
                    <div className="timer-display select-none">
                        {formatTime(timer)}
                    </div>

                    {/* Quick Duration Selectors */}
                    <div className="duration-quick-selectors flex gap-3 justify-center">
                        <button 
                            className={`quick-pill ${mode === 'short' ? 'active' : ''}`}
                            onClick={() => handleModeChange('short')}
                        >
                            5 Min
                        </button>
                        <button 
                            className={`quick-pill ${mode === 'long' ? 'active' : ''}`}
                            onClick={() => handleModeChange('long')}
                        >
                            10 Min
                        </button>
                        <button 
                            className={`quick-pill ${mode === 'pomodoro' ? 'active' : ''}`}
                            onClick={() => handleModeChange('pomodoro')}
                        >
                            25 Min
                        </button>
                    </div>

                    {/* Adjust & Control Buttons */}
                    <div className="controls">
                        <button 
                            className="adjust-circle-btn minus-btn" 
                            onClick={() => adjustTimer(-60)}
                            title="Decrease 1 Min"
                        >
                            —
                        </button>
                        
                        <button className="focus-capsule-btn" onClick={toggleTimer}>
                            {isActive ? 'Pause Focus' : 'Start Focus'}
                        </button>
                        
                        <button 
                            className="adjust-circle-btn plus-btn" 
                            onClick={() => adjustTimer(60)}
                            title="Increase 1 Min"
                        >
                            +
                        </button>
                    </div>
                </div>

                {/* TASKS SECTION */}
                <div className="pomodoro-tasks-section rounded-2xl bg-white/85 p-5 shadow-lg backdrop-blur-sm">
                    <div className="tasks-header">
                        <h3>Tasks</h3>
                        <div className="tasks-options">
                            <i className="fi fi-rr-menu-dots"></i>
                        </div>
                    </div>
                    
                    <div className="tasks-list">
                        {tasks.map((task, index) => (
                            <div 
                                key={task.id} 
                                className={`task-item ${task.active ? 'active-task' : ''} ${task.completed ? 'completed' : ''} ${draggedItemIndex === index ? 'dragging' : ''}`}
                                draggable
                                onDragStart={() => handleDragStart(index)}
                                onDragEnter={() => handleDragEnter(index)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => e.preventDefault()} // Necessary to allow dropping
                            >
                                <i className="fi fi-ss-grip-dots-vertical drag-handle"></i>
                                <div className="task-radio" onClick={() => toggleTaskComplete(task.id)}>
                                    {task.completed && <i className="fi fi-br-check"></i>}
                                </div>
                                <span className="task-text">{task.text}</span>
                                <div className="task-actions">
                                    <i className="fi fi-sr-attribution-pencil edit-icon" title="Edit"></i>
                                    <i className="fi fi-sr-trash-xmark delete-icon" onClick={(e) => deleteTask(e, task.id)} title="Delete"></i>
                                </div>
                            </div>
                        ))}

                        {isAddingTask ? (
                            <div className="add-task-form">
                                <input 
                                    type="text" 
                                    value={newTaskText}
                                    onChange={(e) => setNewTaskText(e.target.value)}
                                    placeholder="What are you working on?"
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && addTask()}
                                />
                                <div className="add-task-actions">
                                    <button onClick={() => setIsAddingTask(false)}>Cancel</button>
                                    <button onClick={addTask}>Save</button>
                                </div>
                            </div>
                        ) : (
                            <div className="add-task-btn" onClick={() => setIsAddingTask(true)}>
                                <div className="plus-icon">
                                    <i className="fi fi-rr-plus"></i>
                                </div>
                                <span>Add another task</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Pomodoro;
